import { useCallback, useEffect, useState } from 'react';

import { showToastMessage } from 'enable-ui';
import { HttpStatusCode } from 'enums/http';

import { GroupPermissions, RoleItem } from './type';

import { trimValue } from 'helpers';
import { useBoolean, useString } from 'hooks';
import { apiCall } from 'hooks/service';
import { debounce } from 'lodash';

import { SUCCESS_MESSAGE } from 'constants/message';

import { PermissionTab } from './enum';

export const usePermissionsController = () => {
  const [groupPermissons, setGroupPermissions] = useState<GroupPermissions>();

  const [roles, setRoles] = useState<RoleItem[]>();

  const [selectedPermissions, setSelectedPermissions] = useState<
    { roleId: number; permissionId: string }[]
  >([]);

  const [activeKey, setActiveKey] = useState<string>(PermissionTab.WEB);

  const isEdit = useBoolean(false);

  const searchValue = useString('');

  useEffect(() => {
    getGroupPermissions();
    getRoles();
  }, []);

  const getGroupPermissions = async (search = '') => {
    const result: { data: GroupPermissions } = await apiCall({
      url: '/group-permissions',
      method: 'GET',
      isLoading: true,
      showError: true,
      data: {
        search,
      },
    });

    if (result) {
      setGroupPermissions(result.data);

      setSelectedPermissions(result.data.rolePermissions);
    }
  };

  const getRoles = async () => {
    const result: RoleItem[] = await apiCall({
      url: '/roles',
      method: 'GET',
      isLoading: true,
      showError: true,
    });

    if (result) {
      setRoles(result);
    }
  };

  const handleChangePermission = (roleId: number, permissionId: string) => {
    const newSelected = [...selectedPermissions];

    const index = newSelected.findIndex(
      (el) => el.roleId === roleId && el.permissionId === permissionId,
    );

    if (index !== -1) {
      newSelected.splice(index, 1);
    } else {
      newSelected.push({ roleId, permissionId });
    }

    setSelectedPermissions(newSelected);
  };

  const onSubmit = async () => {
    const result: { statusCode: HttpStatusCode } = await apiCall({
      url: '/group-permissions/change',
      method: 'POST',
      data: {
        rolePermissions: selectedPermissions,
      },
      isLoading: true,
      showError: true,
    });

    if (result.statusCode === HttpStatusCode.Success) {
      showToastMessage('success', SUCCESS_MESSAGE.UPDATED_PERMISSIONS);

      isEdit.setValue(false);
    }
  };

  const onTabClick = (key: string) => {
    setActiveKey(key);
  };

  const handleCancel = () => {
    isEdit.setValue(false);
    setSelectedPermissions(groupPermissons?.rolePermissions ?? []);
  };

  const onChangeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchValue.setValue(event.target.value);
    debounceSearchValue(event.target.value);
  };

  const debounceSearchValue = useCallback(
    debounce((newSearch: string) => {
      getGroupPermissions(trimValue(newSearch));
    }, 500),
    [],
  );

  return {
    groupPermissons,
    roles,
    selectedPermissions,
    handleChangePermission,
    onSubmit,
    activeKey,
    onTabClick,
    isEdit,
    handleCancel,
    searchValue,
    onChangeSearchValue,
  };
};
